import { QueryClient, useMutation, useQueryClient } from 'react-query'
import { scheduledPostsKeys } from './scheduledPostsKeys'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

export const onMutateUpdateMediaObject = async (
  { id, position }: { id: number; position: number },
  scheduledPostId: number,
  queryClient: QueryClient,
) => {
  await queryClient.cancelQueries(scheduledPostsKeys.show(scheduledPostId))

  const previousPost = queryClient.getQueryData<ScheduledPostShow>(scheduledPostsKeys.show(scheduledPostId))

  queryClient.setQueryData<ScheduledPostShow | undefined>(scheduledPostsKeys.show(scheduledPostId), (old) => {
    if (!old) return undefined

    const updatedMediaObjects = [...old.media_objects].sort((a, b) => a.position - b.position)
    const oldIndex = updatedMediaObjects.findIndex((obj) => obj.id === id)
    const newIndex = position - 1 // Convert 1-based position to 0-based index

    if (oldIndex === -1) return old // If the object is not found, return the original data

    const [movedObject] = updatedMediaObjects.splice(oldIndex, 1)
    updatedMediaObjects.splice(newIndex, 0, movedObject)

    // Update positions for all objects
    updatedMediaObjects.forEach((obj, index) => {
      obj.position = index + 1 // Convert 0-based index back to 1-based position
    })

    return {
      ...old,
      media_objects: updatedMediaObjects,
    }
  })

  return { previousPost }
}

export const onSettledUpdateMediaObject = (scheduledPostId: number, queryClient: any) => {
  queryClient.invalidateQueries(scheduledPostsKeys.show(scheduledPostId))
}

export const useUpdateMediaObjectPosition = ({ scheduledPostId }: { scheduledPostId: number }) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()

  return useMutation(
    'updateMediaObjectPositions',
    async ({ id, position }: { id: number; position: number }) => {
      const res = await sendToApi(`/w/${workspaceId}/scheduled_posts/${scheduledPostId}/media_objects/${id}`, {
        method: 'PATCH',
        body: { scheduled_post_media_object: { position } },
      })
      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error('Error updating media order')
      }
      return res.data
    },
    {
      onSettled: () => onSettledUpdateMediaObject(scheduledPostId, queryClient),
      onMutate: ({ id, position }) => onMutateUpdateMediaObject({ id, position }, scheduledPostId, queryClient),
    },
  )
}
